import React from 'react';
import {App, Button, InputNumber, Segmented, Select} from 'antd';
import {DeleteOutlined, PlusOutlined} from '@ant-design/icons';
import {useI18n} from '../../../i18n/useI18n';
import type {DcaCondition} from '../../../types/dca';
import {buildConditionsText, CONDITION_TYPES, defaultCondition,} from './dcaConditionUtil';

interface Props {
    op: 'AND' | 'OR';
    conditions: DcaCondition[];
    /** 组合变更回调（父组件持有状态） */
    onChange: (op: 'AND' | 'OR', conditions: DcaCondition[]) => void;
}

/**
 * DCA 条件组合编辑器（与/或 + 最多 5 条 + 实时预览）
 * 创建/编辑弹窗与首页计算器共用，保证两处编辑体验一致。
 */
const DcaConditionEditor: React.FC<Props> = ({op, conditions, onChange}) => {
    const {t} = useI18n();
    const {message} = App.useApp();

    const patchCondition = (index: number, patch: Partial<DcaCondition>) => {
        onChange(op, conditions.map((c, i) => (i === index ? {...c, ...patch} : c)));
    };

    const removeCondition = (index: number) => {
        onChange(op, conditions.filter((_, i) => i !== index));
    };

    const addCondition = () => {
        if (conditions.length >= 5) {
            message.warning(t('dca.form.maxReached'));
            return;
        }
        onChange(op, [...conditions, defaultCondition('EMA_CROSS')]);
    };

    /**
     * 方向选择：按条件类型取对应文案 key。
     * 语义为「位置关系」（当前值高于/低于），非「交叉事件」——需求 §11.2 明确
     * 「条件求值只回答当前窗口布尔判定，边沿由 last_signal_state 承载」，
     * 故文案不得暗示"金叉那一刻"的事件语义。
     */
    const renderDir = (cond: DcaCondition, index: number) => {
        const isCross = 'EMA_CROSS' === cond.type || 'SMA_CROSS' === cond.type;
        const upLabel = isCross ? t('dca.dir.golden') : t('dca.dir.crossUp');
        const downLabel = isCross ? t('dca.dir.death') : t('dca.dir.crossDown');
        return (
            <Segmented
                value={cond.dir ?? 'UP'}
                options={[{label: upLabel, value: 'UP'}, {label: downLabel, value: 'DOWN'}]}
                onChange={(v) => patchCondition(index, {dir: v as 'UP' | 'DOWN'})}
            />
        );
    };

    /** 按条件类型渲染参数输入区 */
    const renderCondParams = (cond: DcaCondition, index: number) => {
        switch (cond.type) {
            case 'EMA_CROSS':
            case 'SMA_CROSS':
                return (
                    <div className="dca-cond-params">
                        <span className="dca-cond-label">{t('dca.cond.fast')}</span>
                        <InputNumber min={2} max={500} value={cond.fast}
                                     onChange={(v) => patchCondition(index, {fast: v ?? undefined})}/>
                        <span className="dca-cond-label">{t('dca.cond.slow')}</span>
                        <InputNumber min={2} max={500} value={cond.slow}
                                     onChange={(v) => patchCondition(index, {slow: v ?? undefined})}/>
                        {renderDir(cond, index)}
                    </div>
                );
            case 'PRICE_CROSS_EMA':
                return (
                    <div className="dca-cond-params">
                        <span className="dca-cond-label">{t('dca.cond.period')}</span>
                        <InputNumber min={2} max={500} value={cond.period}
                                     onChange={(v) => patchCondition(index, {period: v ?? undefined})}/>
                        {renderDir(cond, index)}
                    </div>
                );
            case 'RSI':
                return (
                    <div className="dca-cond-params">
                        <span className="dca-cond-label">{t('dca.cond.period')}</span>
                        <InputNumber min={2} max={500} value={cond.period}
                                     onChange={(v) => patchCondition(index, {period: v ?? undefined})}/>
                        <span className="dca-cond-label">{t('dca.cond.mode')}</span>
                        <Segmented
                            value={cond.mode ?? 'CROSS_UP'}
                            options={[{label: t('dca.cond.mode.CROSS_UP'), value: 'CROSS_UP'},
                                {label: t('dca.cond.mode.CROSS_DOWN'), value: 'CROSS_DOWN'}]}
                            onChange={(v) => patchCondition(index, {mode: v as 'CROSS_UP' | 'CROSS_DOWN'})}
                        />
                        <span className="dca-cond-label">{t('dca.cond.level')}</span>
                        <InputNumber min={0.01} max={99.99} step={0.1} value={cond.level}
                                     onChange={(v) => patchCondition(index, {level: v ?? undefined})}/>
                    </div>
                );
            case 'MACD_CROSS':
                return (
                    <div className="dca-cond-params">{renderDir(cond, index)}</div>
                );
            case 'PRICE_BELOW':
                return (
                    <div className="dca-cond-params">
                        <span className="dca-cond-label">{t('dca.cond.value')}</span>
                        <InputNumber min={0.0001} step={0.01} value={cond.value}
                                     onChange={(v) => patchCondition(index, {value: v ?? undefined})}/>
                    </div>
                );
            case 'DRAWDOWN':
                return (
                    <div className="dca-cond-params">
                        <span className="dca-cond-label">{t('dca.cond.windowMin')}</span>
                        <InputNumber min={60} value={cond.windowMin}
                                     onChange={(v) => patchCondition(index, {windowMin: v ?? undefined})}/>
                        <span className="dca-cond-label">{t('dca.cond.pct')}</span>
                        <InputNumber min={0.01} max={99.99} step={0.1} value={cond.pct}
                                     onChange={(v) => patchCondition(index, {pct: v ?? undefined})}/>
                    </div>
                );
            case 'LOW_TOUCH':
                return (
                    <div className="dca-cond-params">
                        <span className="dca-cond-label">{t('dca.cond.periods')}</span>
                        <InputNumber min={2} max={500} value={cond.periods}
                                     onChange={(v) => patchCondition(index, {periods: v ?? undefined})}/>
                    </div>
                );
            default:
                return null;
        }
    };

    return (
        <div className="dca-cond-editor">
            <div className="dca-cond-op">
                <span className="dca-cond-label">{t('dca.form.op')}</span>
                <Segmented
                    value={op}
                    options={[
                        {label: t('dca.form.op.AND'), value: 'AND'},
                        {label: t('dca.form.op.OR'), value: 'OR'},
                    ]}
                    onChange={(v) => onChange(v as 'AND' | 'OR', conditions)}
                />
            </div>
            {conditions.map((cond, index) => (
                <div key={index} className="dca-cond-row">
                    <Select style={{width: 190}} value={cond.type}
                            options={CONDITION_TYPES.map((ct) => ({label: t(`dca.cond.${ct}`), value: ct}))}
                            onChange={(v) => patchCondition(index, defaultCondition(v))}/>
                    {renderCondParams(cond, index)}
                    <Button type="text" danger icon={<DeleteOutlined/>} disabled={conditions.length <= 1}
                            onClick={() => removeCondition(index)}/>
                </div>
            ))}
            <Button type="dashed" block icon={<PlusOutlined/>} disabled={conditions.length >= 5}
                    onClick={addCondition}>
                {t('dca.form.add')}
            </Button>

            <div className="dca-form-label dca-preview-title">{t('dca.form.preview')}</div>
            <div className="dca-preview">{buildConditionsText({op, conditions}, t) || t('dca.form.previewEmpty')}</div>
        </div>
    );
};

export default DcaConditionEditor;
